import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    Box,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Avatar,
} from "@mui/material";
import Loader from "../common/loader";
import TooltipComponent from "../common/Tooltip";
import { alpha } from "@mui/material/styles";
import { ProfileRankBadge } from "../profile/profileIdentityBadges";
import { colors } from "../../theme/colors";
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver";
import { toProfilePath } from "../../universal/v2Routes";
import { isLeaderboardViewerRow } from "./leaderboardFormat";

const ELO_NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
});

function formatElo(value) {
    if (value == null || !Number.isFinite(Number(value))) return "—";
    return ELO_NUMBER_FORMATTER.format(Math.round(Number(value)));
}

function clampReputationScore(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 50;
    return Math.max(0, Math.min(100, Math.round(n)));
}

function headerRowShellSx() {
    const border = `1px solid ${alpha(colors.text.primary, 0.14)}`;
    return {
        "& > .MuiTableCell-root": {
            border: "none",
            borderTop: border,
            borderBottom: border,
            borderLeft: "none",
            borderRight: "none",
            boxShadow: "none",
            backgroundClip: "padding-box",
            backgroundColor: colors.background.dark,
            height: "2.75rem",
            maxHeight: "2.75rem",
            padding: "0 1.5rem",
            verticalAlign: "middle",
            boxSizing: "border-box",
        },
        "& > .MuiTableCell-root:first-of-type": {
            borderLeft: border,
            borderTopLeftRadius: "0.85rem",
            borderBottomLeftRadius: "0.85rem",
        },
        "& > .MuiTableCell-root:last-of-type": {
            borderRight: border,
            borderTopRightRadius: "0.85rem",
            borderBottomRightRadius: "0.85rem",
        },
    };
}

function leaderboardBodyRowShellSx(isFeatured, rankAccent = null, isViewer = false) {
    const border = isViewer
        ? `1px solid ${alpha(colors.accent.main, 0.2)}`
        : rankAccent
        ? `1px solid ${alpha(rankAccent, 0.26)}`
        : `1px solid ${colors.border.default}`;
    const bg = alpha(colors.background.base, 0.75);
    const hoverBg = alpha(colors.background.surface, 0.98);
    const rankAlphaStops = [0.17, 0.125, 0.08, 0.04, 0.012];
    const rankGradientCells = rankAccent
        ? Object.fromEntries(
            rankAlphaStops.map((opacity, index) => {
                const nextOpacity = rankAlphaStops[index + 1] ?? 0;
                return [
                    `& > .MuiTableCell-root:nth-of-type(${index + 1})`,
                    {
                        background: `linear-gradient(90deg, ${alpha(rankAccent, opacity)}, ${alpha(rankAccent, nextOpacity)}), linear-gradient(${bg}, ${bg})`,
                    },
                ];
            }),
        )
        : {};

    return {
        background: isViewer
            ? `linear-gradient(90deg, ${alpha(colors.primary.main, 0.075)} 0%, rgba(255,255,255,0.014) 38%, transparent 78%)`
            : "transparent",
        "& > .MuiTableCell-root": {
            border: "none",
            borderTop: border,
            borderBottom: border,
            borderLeft: "none",
            borderRight: "none",
            boxShadow: "none",
            backgroundClip: "padding-box",
            backgroundColor: rankAccent || isViewer ? "transparent" : bg,
            height: isFeatured ? "6.25rem" : "4rem",
            maxHeight: isFeatured ? "6.25rem" : "4rem",
            padding: "0 1.5rem",
            verticalAlign: "middle",
            transition: "background-color 0.15s ease, filter 0.15s ease",
            boxSizing: "border-box",
        },
        ...rankGradientCells,
        "& > .MuiTableCell-root:first-of-type": {
            position: "relative",
            borderLeft: border,
            borderTopLeftRadius: "0.85rem",
            borderBottomLeftRadius: "0.85rem",
            ...(isViewer && {
                "&::before": {
                    content: '\"\"',
                    position: "absolute",
                    left: "5px",
                    top: "24%",
                    bottom: "24%",
                    width: "2px",
                    borderRadius: "999px",
                    background: `linear-gradient(180deg, transparent, ${colors.accent.main}, transparent)`,
                    boxShadow: `0 0 9px ${alpha(colors.primary.main, 0.28)}`,
                    pointerEvents: "none",
                },
            }),
        },
        "& > .MuiTableCell-root:last-of-type": {
            borderRight: border,
            borderTopRightRadius: "0.85rem",
            borderBottomRightRadius: "0.85rem",
        },
        "&:hover > .MuiTableCell-root": {
            ...(rankAccent || isViewer
                ? { filter: "brightness(1.08)" }
                : { backgroundColor: hoverBg }),
        },
    };
}

const headCellBaseSx = {
    fontFamily: "'JetBrains Mono', monospace",
    color: colors.text.primary,
    fontWeight: 800,
    fontSize: "0.8rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    border: "none",
};

function mobileRowShellSx(isFeatured = false, rankAccent = null, isViewer = false) {
    return {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "0.65rem",
        width: "100%",
        px: "0.85rem",
        py: isFeatured ? "1.25rem" : "0.75rem",
        minHeight: isFeatured ? "6.25rem" : "4rem",
        mb: "0.45rem",
        borderRadius: "0.85rem",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        border: isViewer
            ? `1px solid ${alpha(colors.accent.main, 0.2)}`
            : rankAccent
            ? `1px solid ${alpha(rankAccent, 0.26)}`
            : `1px solid ${colors.border.default}`,
        background: isViewer
            ? `linear-gradient(90deg, ${alpha(colors.primary.main, 0.09)} 0%, rgba(255,255,255,0.014) 48%, transparent 100%), linear-gradient(${alpha(colors.background.base, 0.82)}, ${alpha(colors.background.base, 0.82)})`
            : rankAccent
            ? `linear-gradient(90deg, ${alpha(rankAccent, 0.17)} 0%, ${alpha(rankAccent, 0.07)} 52%, transparent 100%), linear-gradient(${alpha(colors.background.base, 0.75)}, ${alpha(colors.background.base, 0.75)})`
            : alpha(colors.background.base, 0.75),
        ...(isViewer && {
            "&::before": {
                content: '\"\"',
                position: "absolute",
                left: "5px",
                top: "24%",
                bottom: "24%",
                width: "2px",
                borderRadius: "999px",
                background: `linear-gradient(180deg, transparent, ${colors.accent.main}, transparent)`,
                boxShadow: `0 0 9px ${alpha(colors.primary.main, 0.28)}`,
                pointerEvents: "none",
            },
        }),
        transition: "background-color 0.15s ease, filter 0.15s ease",
        "&:hover": {
            ...(rankAccent || isViewer
                ? { filter: "brightness(1.08)" }
                : { backgroundColor: alpha(colors.background.surface, 0.98) }),
        },
    };
}

export default function LeaderboardTable({
    playersData = [],
    user,
    isLoading = false,
    hasAnyRows = false,
    showLoadMore = false,
    onLoadMore,
    beyondPodiumPlaceholderText = null,
    scope = "global",
}) {
    const history = useHistory();
    const location = useLocation();

    const handleIntersect = useCallback(() => {
        if (!isLoading && showLoadMore && typeof onLoadMore === "function") {
            onLoadMore();
        }
    }, [isLoading, showLoadMore, onLoadMore]);

    const sentinelRef = useIntersectionObserver(handleIntersect);

    if (user && !hasAnyRows && isLoading) {
        return (
            <Box sx={{ width: "100%" }}>
                {Array.from({ length: 8 }).map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 2,
                            py: 1,
                            borderRadius: "0.85rem",
                            mb: 0.5,
                            backgroundColor: alpha(colors.background.base, 0.75),
                            border: `1px solid ${colors.border.default}`,
                            height: "3rem",
                        }}
                    >
                        <Skeleton
                            variant="text"
                            width={24}
                            sx={{ bgcolor: alpha(colors.text.primary, 0.08) }}
                        />
                        <Skeleton
                            variant="circular"
                            width={28}
                            height={28}
                            sx={{ bgcolor: alpha(colors.text.primary, 0.08), flexShrink: 0 }}
                        />
                        <Skeleton
                            variant="text"
                            width={120}
                            sx={{ bgcolor: alpha(colors.text.primary, 0.08) }}
                        />
                        <Skeleton
                            variant="text"
                            width={60}
                            sx={{ ml: "auto", bgcolor: alpha(colors.text.primary, 0.08) }}
                        />
                    </Box>
                ))}
            </Box>
        );
    }

    const renderMobileCards = () => (
        <Box
            sx={{
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                width: "100%",
                mb: 2,
            }}
        >
            {playersData.map((row) => {
                const isMe = isLeaderboardViewerRow(row, user);
                const isFeatured = Number(row?.rank) >= 1 && Number(row?.rank) <= 3;
                const profileLink = toProfilePath(row?._id, location.pathname);

                return (
                    <Box
                        key={`${row?.userId || row?._id || row?.rank}`}
                        data-leaderboard-viewer-me={isMe ? "" : undefined}
                        role={profileLink ? "button" : undefined}
                        tabIndex={profileLink ? 0 : undefined}
                        onClick={
                            profileLink
                                ? () => history.push(profileLink)
                                : undefined
                        }
                        onKeyDown={
                            profileLink
                                ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          history.push(profileLink);
                                      }
                                  }
                                : undefined
                        }
                        sx={[
                            mobileRowShellSx(isFeatured, null, isMe),
                            profileLink && { cursor: "pointer" },
                        ]}
                    >
                        <ProfileRankBadge rank={row?.rank} scope={scope} />
                        <Avatar
                            src={row?.picture || undefined}
                            alt={row?.nickname || "Player"}
                            sx={{
                                width: "2rem",
                                height: "2rem",
                                flexShrink: 0,
                            }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "0.92rem",
                                    color: colors.text.primary,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {row?.nickname || "Unknown"}
                                {isMe ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            ml: "0.45rem",
                                            px: "0.38rem",
                                            py: "0.08rem",
                                            borderRadius: "5px",
                                            border: `1px solid ${alpha(colors.accent.main, 0.2)}`,
                                            background: alpha(colors.primary.main, 0.065),
                                            color: colors.accent.soft,
                                            fontSize: "0.55rem",
                                            fontWeight: 750,
                                            letterSpacing: "0.08em",
                                            lineHeight: 1.35,
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        YOU
                                    </Box>
                                ) : null}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.35rem",
                                    mt: "0.15rem",
                                    minWidth: 0,
                                }}
                            >
                                {row?.flagImageUrl ? (
                                    <Box
                                        component="img"
                                        src={row.flagImageUrl}
                                        alt=""
                                        aria-hidden="true"
                                        sx={{
                                            width: 16,
                                            height: 12,
                                            borderRadius: "2px",
                                            objectFit: "cover",
                                            flexShrink: 0,
                                        }}
                                    />
                                ) : null}
                                <Typography
                                    sx={{
                                        fontSize: "0.72rem",
                                        fontWeight: 500,
                                        color: colors.text.secondary,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {row?.country || "—"}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography
                            sx={{
                                minWidth: "4.5rem",
                                flexShrink: 0,
                                color: colors.text.primary,
                                fontFamily: "'Orbitron', sans-serif",
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                lineHeight: 1,
                                letterSpacing: "0.025em",
                                textAlign: "center",
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {formatElo(row?.elo)}
                        </Typography>
                    </Box>
                );
            })}
            {beyondPodiumPlaceholderText ? (
                <Box
                    sx={{
                        ...mobileRowShellSx(false),
                        justifyContent: "center",
                        cursor: "default",
                        "&:hover": {
                            backgroundColor: alpha(colors.background.base, 0.75),
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: colors.text.secondary,
                            letterSpacing: "0.04em",
                            textAlign: "center",
                        }}
                    >
                        {beyondPodiumPlaceholderText}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {user && hasAnyRows && renderMobileCards()}

            {user && hasAnyRows && (
                <TableContainer
                    sx={{
                        display: { xs: "none", md: "block" },
                        width: "100%",
                        mb: 2,
                        backgroundColor: "transparent",
                        border: "none",
                    }}
                >
                    <Table
                        sx={{
                            width: "100%",
                            tableLayout: "fixed",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                            "& .MuiTableCell-root": {
                                boxShadow: "none",
                                outline: "none",
                                backgroundImage: "none",
                            },
                        }}
                    >
                        <TableHead sx={{ backgroundColor: "transparent" }}>
                            <TableRow sx={headerRowShellSx()}>
                                <TableCell align="center" sx={{ ...headCellBaseSx, width: "10%" }}>
                                    Rank
                                </TableCell>
                                <TableCell sx={{ ...headCellBaseSx, width: "35%" }}>Player</TableCell>
                                <TableCell align="center" sx={{ ...headCellBaseSx, width: "25%" }}>ELO</TableCell>
                                <TableCell align="center" sx={{ ...headCellBaseSx, width: "15%" }}>Country</TableCell>
                                <TableCell align="center" sx={{ ...headCellBaseSx, width: "15%" }}>Reputation</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {playersData.map((row) => {
                                const isMe = isLeaderboardViewerRow(row, user);
                                const isFeatured = Number(row?.rank) >= 1 && Number(row?.rank) <= 3;
                                const profileLink = toProfilePath(row?._id, location.pathname);
                                const reputationScore = clampReputationScore(row?.reputationScore);

                                return (
                                    <TableRow
                                        key={`${row?.userId || row?._id || row?.rank}`}
                                        data-leaderboard-viewer-me={
                                            isMe ? "" : undefined
                                        }
                                        hover
                                        onClick={
                                            profileLink
                                                ? () => history.push(profileLink)
                                                : undefined
                                        }
                                        sx={[
                                            leaderboardBodyRowShellSx(isFeatured, null, isMe),
                                            profileLink && { cursor: "pointer" },
                                        ]}
                                    >
                                        <TableCell align="center">
                                            <ProfileRankBadge rank={row?.rank} scope={scope} />
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.25,
                                                }}
                                            >
                                                <Avatar
                                                    src={row?.picture || undefined}
                                                    alt={row?.nickname || "Player"}
                                                    sx={{
                                                width: "2rem",
                                                height: "2rem",
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {row?.nickname || "Unknown"}
                                                        {isMe ? (
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    ml: "0.55rem",
                                                                    px: "0.42rem",
                                                                    py: "0.1rem",
                                                                    borderRadius: "5px",
                                                                    border: `1px solid ${alpha(colors.accent.main, 0.2)}`,
                                                                    background: alpha(colors.primary.main, 0.065),
                                                                    color: colors.accent.soft,
                                                                    fontSize: "0.56rem",
                                                                    fontWeight: 750,
                                                                    letterSpacing: "0.08em",
                                                                    lineHeight: 1.35,
                                                                    verticalAlign: "middle",
                                                                }}
                                                            >
                                                                YOU
                                                            </Box>
                                                        ) : null}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography
                                                sx={{
                                                    color: colors.text.primary,
                                                    fontFamily: "'Orbitron', sans-serif",
                                                    fontSize: "0.93rem",
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                    letterSpacing: "0.025em",
                                                    fontVariantNumeric: "tabular-nums",
                                                }}
                                            >
                                                {formatElo(row?.elo)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {row?.flagImageUrl ? (
                                                <TooltipComponent
                                                    title={row?.country || "Unknown country"}
                                                    placement="top"
                                                >
                                                    <Box
                                                        component="span"
                                                        tabIndex={0}
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            cursor: "help",
                                                            outline: "none",
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={row.flagImageUrl}
                                                            alt={row?.country || "Country"}
                                                            sx={{
                                                                display: "block",
                                                                width: 21,
                                                                height: 15,
                                                                borderRadius: "4px",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    </Box>
                                                </TooltipComponent>
                                            ) : (
                                                <Typography
                                                    component="span"
                                                    sx={{ color: colors.text.secondary }}
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: colors.text.primary,
                                                }}
                                            >
                                                {reputationScore}/100
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {beyondPodiumPlaceholderText ? (
                                <TableRow
                                    sx={[
                                        leaderboardBodyRowShellSx(false),
                                        {
                                            "&:hover > .MuiTableCell-root": {
                                                backgroundColor: alpha(
                                                    colors.background.base,
                                                    0.75
                                                ),
                                            },
                                        },
                                    ]}
                                >
                                    <TableCell
                                        colSpan={6}
                                        sx={{
                                            textAlign: "center",
                                            cursor: "default",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 13, sm: 14 },
                                                fontWeight: 500,
                                                color: colors.text.secondary,
                                                letterSpacing: "0.04em",
                                            }}
                                        >
                                            {beyondPodiumPlaceholderText}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {user && hasAnyRows && (
                <Box ref={sentinelRef} sx={{ width: "100%", height: "1px" }} />
            )}

            {isLoading && hasAnyRows && (
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.2,
                        py: 3,
                    }}
                >
                    <Loader size={28} />
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            color: colors.text.primary,
                            letterSpacing: "0.04em",
                        }}
                    >
                        Loading players…
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
